import React from 'react';
import {
  Dashboard,
  Language,
  Assignment,
  Inventory2,
  ShoppingCart,
  MonitorHeart,
  ChevronRight,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useProjectWorkflow, WORKFLOW_STEPS } from '../../contexts/ProjectWorkflowContext';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { clientPlanService } from '../../services/clientPlanService';
import { WorkflowStepId } from '../../types/projectWorkflow';

function isValidDocUrl(value?: string | null): boolean {
  const v = (value || '').trim();
  if (!v) return false;
  try {
    const parsed = new URL(v);
    return /^https?:$/i.test(parsed.protocol);
  } catch {
    return false;
  }
}

const STEP_ICONS: Record<WorkflowStepId, React.ReactNode> = {
  overview: <Dashboard sx={{ fontSize: 14 }} />,
  sites: <Language sx={{ fontSize: 14 }} />,
  plan: <Assignment sx={{ fontSize: 14 }} />,
  order: <Inventory2 sx={{ fontSize: 14 }} />,
  cart: <ShoppingCart sx={{ fontSize: 14 }} />,
  status: <MonitorHeart sx={{ fontSize: 14 }} />,
};

const ProjectWorkflowTabs: React.FC = () => {
  const { selectedProject, activeStep, doneSteps, goToStep } = useProjectWorkflow();
  const { cart, clearCart, refreshCart } = useCart(selectedProject?.id);
  const { isClient, isAgency } = useAuth();
  const usesPaymentCheckout = isClient || isAgency;
  const [searchParams] = useSearchParams();

  // Navigating to Explore Sites should start a FRESH order when a saved order is
  // currently bound (planId in the URL) — clear that order's sites out of the
  // shared cart first. An unbound, in-progress selection (no planId) is kept.
  const handleStepClick = (step: WorkflowStepId) => {
    if (step === 'sites' && searchParams.get('planId')) {
      void (async () => {
        if (cart && cart.cartItems.length > 0) {
          await clearCart();
          await refreshCart();
        }
        goToStep('sites', { fresh: true });
      })();
      return;
    }
    goToStep(step);
  };

  // Cart & Checkout (Stripe payment) for clients and agencies. Admin / Super Admin
  // place the order directly from the Place Order step, so the cart step is hidden.
  const steps = React.useMemo(
    () => (usesPaymentCheckout ? WORKFLOW_STEPS : WORKFLOW_STEPS.filter((s) => s.id !== 'cart')),
    [usesPaymentCheckout]
  );

  // Saved orders for this project are the PERSISTENT signal that the
  // Explore Sites + Create Order steps are complete. Basing completion on this
  // (instead of the transient working cart) keeps the checkmarks stable when
  // opening/closing a saved order — the cart filling/clearing no longer flips
  // the step indicators (which caused the "laggy" flicker).
  const { data: savedPlansResp } = useQuery({
    queryKey: ['wf-saved-plans', selectedProject?.clientId, selectedProject?.id],
    queryFn: () =>
      clientPlanService.getPlans({
        clientId: selectedProject!.clientId,
        projectId: selectedProject!.id,
        status: 'ACTIVE',
      }),
    enabled: Boolean(selectedProject?.id) && Boolean(selectedProject?.clientId),
    staleTime: 30 * 1000,
  });
  const hasSavedOrders = (savedPlansResp?.data?.length ?? 0) > 0;

  // Completion derived from real (cached) state so checkmarks stay stable across
  // navigation instead of flickering with ephemeral flags. Manual flags
  // (checkout, status) are OR'd in on top.
  const effectiveDone = React.useMemo<Record<WorkflowStepId, boolean>>(() => {
    const items = cart?.cartItems || [];
    // A brand-new (not-yet-saved) selection in the cart also counts as having sites.
    const hasSites = items.length > 0 || hasSavedOrders;
    const allDocs =
      items.length > 0 && items.every((it) => isValidDocUrl(it.contentUrl || it.contentDocUrl));
    return {
      overview: doneSteps.overview || hasSites,
      sites: doneSteps.sites || hasSites,
      plan: doneSteps.plan || hasSavedOrders,
      order: doneSteps.order || allDocs,
      cart: doneSteps.cart,
      status: doneSteps.status,
    };
  }, [cart, doneSteps, hasSavedOrders]);

  if (!selectedProject) return null;

  const doneCount = steps.filter((s) => effectiveDone[s.id]).length;
  const progress = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="wf-step-wrap">
      <div className="wf-step-tabs">
        {steps.map((step, i) => {
          const done = effectiveDone[step.id];
          const active = activeStep === step.id;
          const num = i === 0 ? null : i;
          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                className={`wf-stab${active ? ' on' : ''}`}
                onClick={() => handleStepClick(step.id)}
              >
                <span className={`wf-sbdg${done ? ' done' : active ? ' actv' : ' pend'}`}>
                  {done ? '✓' : num ?? ''}
                </span>
                {i > 0 && STEP_ICONS[step.id]}
                <span>{step.label}</span>
              </button>
              {i < steps.length - 1 && (
                <ChevronRight className="wf-sarr" sx={{ fontSize: 12 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <div className="wf-spbar">
        <div className="wf-spfill" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

export default ProjectWorkflowTabs;
